import { useCallback, useState } from "react";
import { apiClient } from "../lib/api";

interface VoteResponse {
  vote: unknown;
  vote_count: number;
}

interface RemoveVoteResponse {
  ok: boolean;
  vote_count: number;
}

interface UseVote {
  castVote: (partyId: number) => Promise<number>;
  removeVote: (partyId: number) => Promise<number>;
  isSubmitting: boolean;
  error: string | null;
}

export function useVote(): UseVote {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const castVote = useCallback(async (partyId: number): Promise<number> => {
    setIsSubmitting(true);
    setError(null);
    try {
      const data = await apiClient.post<VoteResponse>("/api/votes", {
        party_id: partyId,
      });
      return data.vote_count;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to cast vote");
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const removeVote = useCallback(async (partyId: number): Promise<number> => {
    setIsSubmitting(true);
    setError(null);
    try {
      const data = await apiClient.delete<RemoveVoteResponse>(
        `/api/votes/${partyId}`,
      );
      return data.vote_count;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to remove vote");
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  return { castVote, removeVote, isSubmitting, error };
}
